import * as turf from '@turf/turf'
import { buffer } from '@turf/turf'
import { HelmFeatureGroup, helmFeatureGroupSchema } from 'api-validations/helm/guidance-path'
import { GuidancePrimitive } from 'api-validations/locutus/guidance'
import { color as colorFunc } from 'd3-color'
import type { FeatureLike } from 'ol/Feature'
import type { Geometry as OlGeometry } from 'ol/geom'
import { Circle as CircleGeom, LineString, MultiPolygon, Point, Polygon } from 'ol/geom'
import { fromLonLat, toLonLat } from 'ol/proj'
import RenderFeature from 'ol/render/Feature'
import { Fill, RegularShape, Stroke, Style, Text } from 'ol/style'
import Circle from 'ol/style/Circle'
import type { StyleLike } from 'ol/style/Style'
import { Geo, isNil, isNonNil } from 'utils'

import {
  DrawMode,
  DrawScene,
  getDataTools,
  getDrawScene,
  getSelectedId,
  getZoneById,
  LassoMode
} from '../map-state-store'
import { Z_INDEX } from './common'

export const SCENE_FOR_KIND: Readonly<Record<string, DrawScene>> = {
  edge: DrawScene.EdgeSelection,
  hole: DrawScene.HoleSelection,
  corner: DrawScene.CornerSelection,
  // Corridor strips between regions have no selection scene of their own;
  // they belong to the combined overrides view only.
  corridor: DrawScene.AllOverrides,
  // Carved zone polygons parked in the view-only layer while the Lines tab is
  // active. Hidden in DrawScene.Boundary; restored to the editable source
  // before the scene switches back to Lines.
  carvedZone: DrawScene.Lines
}

function pixelsToMeters(pixels: number, resolution: number): number {
  // For EPSG:3857, resolution is in meters/pixel at the given scale
  return pixels * resolution
}

export const defaultZoneStyle = new Style({
  stroke: new Stroke({ color: 'black', width: 1 }),
  fill: new Fill({ color: 'rgba(200,200,200,0.3)' })
})

/**
 * Black wash signalling the lasso region is masked out; data of interest lies outside.
 */
export const excludeLassoStyle = new Style({
  stroke: new Stroke({ color: 'black', width: 1 }),
  fill: new Fill({ color: 'rgba(0,0,0,0.4)' })
})

export const getStyle = (opts: { color: string; active: boolean; resolution: number }) => {
  const colorRGB = colorFunc(opts.color)?.rgb()
  if (isNil(colorRGB)) {
    return defaultZoneStyle
  }
  const { r, b, g } = colorRGB
  const fill = new Fill({
    color: `rgba(${r}, ${g}, ${b}, ${opts.active ? 0.6 : 0.2})`
  })
  const stroke = new Stroke({
    color: `rgba(${r}, ${g}, ${b}, 1)`,
    width: opts.active ? 6 : 4
  })

  // Hole-ring highlighting is intentionally not applied here — that's the job
  // of `getHoleSelectionStyle`, used only when the active scene is
  // `HoleSelection`. In `ZoneEditing` the polygon is rendered with the zone
  // color and inner rings show as transparent cutouts in the fill.
  return new Style({
    geometry: (feature) => {
      if (feature instanceof RenderFeature) return
      const geoFeature = Geo.GeoJSON.fromOpenlayersFeature(feature)
      if (!geoFeature) return undefined
      const inset = buffer(geoFeature, -pixelsToMeters(1.5, opts.resolution), {
        units: 'meters'
      })
      if (inset === undefined) return feature.getGeometry()
      return Geo.GeoJSON.toOpenlayersGeometry(inset.geometry)
    },
    fill: fill,
    stroke: stroke,
    zIndex: opts.active ? 2 : 1
  })
}

// Shared building blocks used by both the saved-feature and draw-preview styles.

const centerMarkerStyle = (center: [number, number], color: string, active: boolean) =>
  new Style({
    geometry: new Point(center),
    image: new Circle({
      radius: active ? 6 : 4,
      fill: new Fill({ color }),
      stroke: new Stroke({ color: '#FFFFFF', width: 2 })
    })
  })

export type FormatDistance = (feet: number) => string

const radiusLabelStyle = (
  midpoint: [number, number],
  radiusFt: number,
  bold: boolean,
  formatDistance: FormatDistance | undefined
) =>
  new Style({
    geometry: new Point(midpoint),
    text: new Text({
      text: formatDistance?.(radiusFt) ?? `${Math.round(radiusFt)} ft`,
      font: `${bold ? 'bold ' : ''}14px sans-serif`,
      fill: new Fill({ color: '#FFFFFF' }),
      stroke: new Stroke({ color: 'rgba(0, 0, 0, 0.75)', width: 3 }),
      offsetY: -10
    })
  })

type RadialPivotGeom = {
  center: [number, number]
  perimeter: [number, number]
  radius: number
  radiusFt: number
  midpoint: [number, number]
}

const radialPivotGeom = (coords: number[][]): RadialPivotGeom | undefined => {
  if (coords.length < 2) return undefined
  const center = coords[0] as [number, number]
  const perimeter = coords[coords.length - 1] as [number, number]
  const radius = Math.hypot(perimeter[0] - center[0], perimeter[1] - center[1])
  const radiusFt = turf.distance(turf.point(toLonLat(center)), turf.point(toLonLat(perimeter)), {
    units: 'feet'
  })
  const midpoint: [number, number] = [
    (center[0] + perimeter[0]) / 2,
    (center[1] + perimeter[1]) / 2
  ]
  return { center, perimeter, radius, radiusFt, midpoint }
}

/**
 * Style layers for a RadialPivot: the circle, a center marker, and a radius
 * label at the midpoint. Used by both saved-feature and draw-preview styles.
 */
const radialPivotDecoration = (
  parts: RadialPivotGeom,
  color: string,
  active: boolean,
  labelBold: boolean,
  formatDistance: FormatDistance | undefined
): Style[] => [
  new Style({
    geometry: new CircleGeom(parts.center, parts.radius),
    stroke: new Stroke({ color, width: active ? 4 : 2 })
  }),
  centerMarkerStyle(parts.center, color, active),
  radiusLabelStyle(parts.midpoint, parts.radiusFt, labelBold, formatDistance)
]

/**
 * Style layers for an AHeading extension: dashed line projecting from the
 * anchor point in the given direction, plus a triangular arrow head at the
 * far end pointing along the heading. `direction` may be a heading in degrees
 * (0 = N, 90 = E) or a raw vector `{ dx, dy }` in map coords.
 */
const aHeadingExtension = (
  anchor: [number, number],
  direction: { headingDeg: number } | { dx: number; dy: number },
  color: string,
  width: number,
  lengthM: number
): Style[] => {
  let end: [number, number]
  let angleRad: number
  if ('headingDeg' in direction) {
    const rad = (direction.headingDeg * Math.PI) / 180
    const dx = Math.sin(rad) * lengthM
    const dy = Math.cos(rad) * lengthM
    end = [anchor[0] + dx, anchor[1] + dy]
    angleRad = Math.atan2(dy, dx)
  } else {
    const len = Math.hypot(direction.dx, direction.dy)
    if (len === 0) return []
    const scale = lengthM / len
    end = [anchor[0] + direction.dx * scale, anchor[1] + direction.dy * scale]
    angleRad = Math.atan2(direction.dy, direction.dx)
  }
  // OL RegularShape rotation is clockwise from the top of the icon; convert
  // from cartesian (counter-clockwise from east) so the arrow tip points
  // along A→B.
  const rotation = Math.PI / 2 - angleRad
  return [
    new Style({
      geometry: new LineString([anchor, end]),
      stroke: new Stroke({ color, width, lineDash: [10, 10] })
    }),
    new Style({
      geometry: new Point(end),
      image: new RegularShape({
        points: 3,
        radius: Math.max(width * 2.5, 7),
        rotation,
        fill: new Fill({ color }),
        stroke: new Stroke({ color, width: 1 })
      })
    })
  ]
}

/**
 * White halo + colored line stroke at the zIndexes primitives need to sit
 * above zone polygon fills on the editable layer. `haloGeometry` picks the
 * shape the halo traces — a CircleGeom around a pivot, the A→B segment for an
 * AHeading, or the feature's own geometry (undefined) for Curve/ABLine.
 * Halo is only emitted when the primitive belongs to the active zone.
 */
const PRIMITIVE_HALO_COLOR = '#FFFFFF'
const PRIMITIVE_HALO_WIDTH = 10

/**
 * White halo stroke used to highlight primitives — the active zone on the
 * editable layer, the current selection on the view-only overlay. `geometry`
 * is the shape the halo traces (undefined = the feature's own geometry).
 * Consolidates every primitive halo so the color, width, and construction
 * stay consistent across layers.
 */
const primitiveHaloStyle = (opts: {
  geometry?: OlGeometry
  zIndex: number
  width?: number
}): Style =>
  new Style({
    ...(opts.geometry !== undefined && { geometry: opts.geometry }),
    stroke: new Stroke({ color: PRIMITIVE_HALO_COLOR, width: opts.width ?? PRIMITIVE_HALO_WIDTH }),
    zIndex: opts.zIndex
  })

const editableLineChrome = (opts: {
  color: string
  active: boolean
  haloGeometry?: OlGeometry
}): Style[] => {
  const line = new Style({
    stroke: new Stroke({ color: opts.color, width: opts.active ? 6 : 4 }),
    zIndex: Z_INDEX.DRAW_TOOL + 6
  })
  if (!opts.active) return [line]
  const halo = primitiveHaloStyle({
    geometry: opts.haloGeometry,
    zIndex: Z_INDEX.DRAW_TOOL + 5
  })
  return [halo, line]
}

/**
 * Style layers for a RadialPivot: the circle, a center marker, and a radius
 * label at the midpoint. Falls back to a plain line stroke when the two
 * coordinates can't form a valid pivot. Exported for `createViewOnlyStyle`,
 * which paints saved pivots on the overlay layer.
 */
export const radialPivotStyle = (
  geom: LineString,
  color: string,
  active: boolean,
  formatDistance: FormatDistance | undefined
): Style[] => {
  const parts = radialPivotGeom(geom.getCoordinates())
  if (isNil(parts)) {
    return [new Style({ stroke: new Stroke({ color, width: active ? 6 : 4 }) })]
  }
  return radialPivotDecoration(parts, color, active, active, formatDistance)
}

/**
 * Per-feature style for a saved AHeading: A→B segment, endpoint markers, and
 * the dashed heading extension. Point-geometry AHeadings render as a single
 * anchor marker + extension.
 */
const aHeadingFeatureStyleFn = (opts: {
  getActiveZoneId: () => string | undefined
  getHeadingExtensionLength: () => number
}): StyleLike => {
  return (feature: FeatureLike) => {
    if (getDrawScene() !== DrawScene.Lines) return []
    const geom = feature instanceof RenderFeature ? null : feature.getGeometry()
    const zone = feature.get('zone') as string | undefined
    const color = (isNonNil(zone) ? getZoneById(zone) : null)?.color ?? 'black'
    const active = zone === opts.getActiveZoneId()
    const lengthM = opts.getHeadingExtensionLength()
    const bumped = (style: Style): Style => {
      style.setZIndex(Z_INDEX.DRAW_TOOL + 6)
      return style
    }
    if (geom instanceof LineString) {
      const coords = geom.getCoordinates() as [number, number][]
      const anchor = coords[0]
      const end = coords.at(-1)
      if (isNil(anchor) || isNil(end)) return []
      const chrome = editableLineChrome({
        color,
        active,
        haloGeometry: new LineString([anchor, end])
      })
      return [
        ...chrome,
        bumped(centerMarkerStyle(anchor, color, active)),
        bumped(centerMarkerStyle(end, color, active)),
        ...aHeadingExtension(
          anchor,
          { dx: end[0] - anchor[0], dy: end[1] - anchor[1] },
          color,
          active ? 3 : 2,
          lengthM
        ).map(bumped)
      ]
    }
    if (geom instanceof Point) {
      const headingDeg = feature.get('headingDeg') as number | undefined
      if (isNil(headingDeg)) return []
      const anchor = geom.getCoordinates() as [number, number]
      return [
        bumped(centerMarkerStyle(anchor, color, active)),
        ...aHeadingExtension(anchor, { headingDeg }, color, active ? 3 : 2, lengthM).map(bumped)
      ]
    }
    return []
  }
}

/**
 * Per-feature style for a saved RadialPivot. Attached via `feature.setStyle`
 * so the circle + radius label survive even when the layer's conditional
 * style function doesn't pick up the `type` property (e.g. after a GeoJSON
 * round-trip through the command manager).
 */
const radialPivotFeatureStyleFn = (opts: {
  getActiveZoneId: () => string | undefined
  getFormatDistance: () => FormatDistance | undefined
}): StyleLike => {
  return (feature: FeatureLike) => {
    if (getDrawScene() !== DrawScene.Lines) return []
    const geom = feature instanceof RenderFeature ? null : feature.getGeometry()
    if (!(geom instanceof LineString)) return []
    const zone = feature.get('zone') as string | undefined
    const color = (isNonNil(zone) ? getZoneById(zone) : null)?.color ?? 'black'
    const active = zone === opts.getActiveZoneId()
    const parts = radialPivotGeom(geom.getCoordinates())
    const chrome = editableLineChrome({
      color,
      active,
      haloGeometry: isNonNil(parts) ? new CircleGeom(parts.center, parts.radius) : undefined
    })
    const decoration = radialPivotStyle(geom, color, active, opts.getFormatDistance()).map(
      (style) => {
        style.setZIndex(Z_INDEX.DRAW_TOOL + 6)
        return style
      }
    )
    return [...chrome, ...decoration]
  }
}

/**
 * Dispatch for the per-feature style attached with `feature.setStyle` to a
 * committed guidance primitive. Returns `undefined` for primitives that don't
 * need per-feature chrome (Curve/ABLine render via `createZoneLayerStyle`).
 */
export const primitiveFeatureStyle = (opts: {
  type: GuidancePrimitive
  getActiveZoneId: () => string | undefined
  getFormatDistance: () => FormatDistance | undefined
  getHeadingExtensionLength: () => number
}): StyleLike | undefined => {
  switch (opts.type) {
    case GuidancePrimitive.RadialPivot:
      return radialPivotFeatureStyleFn(opts)
    case GuidancePrimitive.AHeading:
      return aHeadingFeatureStyleFn({
        getActiveZoneId: opts.getActiveZoneId,
        getHeadingExtensionLength: opts.getHeadingExtensionLength
      })
    default:
      return undefined
  }
}

const dotStyle = (color: string): Style =>
  new Style({ image: new Circle({ radius: 4, fill: new Fill({ color }) }) })

const radialPivotDrawStyle = (
  activeColor: string,
  getFormatDistance: (() => FormatDistance | undefined) | undefined
): StyleLike => {
  return (feature: FeatureLike) => {
    const geom = feature.getGeometry()
    if (geom instanceof Point) return [dotStyle(activeColor)]
    if (!(geom instanceof LineString)) return []
    const parts = radialPivotGeom(geom.getCoordinates())
    if (isNil(parts)) return [dotStyle(activeColor)]
    return [
      new Style({
        geometry: new CircleGeom(parts.center, parts.radius),
        stroke: new Stroke({ color: 'rgb(0, 0, 0)', width: 4 })
      }),
      new Style({
        geometry: new CircleGeom(parts.center, parts.radius),
        stroke: new Stroke({ color: activeColor, width: 2 })
      }),
      new Style({
        geometry: new Point(parts.center),
        image: new Circle({
          radius: 5,
          fill: new Fill({ color: activeColor }),
          stroke: new Stroke({ color: '#FFFFFF', width: 2 })
        })
      }),
      radiusLabelStyle(parts.midpoint, parts.radiusFt, true, getFormatDistance?.())
    ]
  }
}

const aHeadingDrawStyle = (
  activeColor: string,
  getHeadingExtensionLength: () => number
): StyleLike => {
  return (feature: FeatureLike) => {
    const geom = feature.getGeometry()
    if (geom instanceof Point) return [dotStyle(activeColor)]
    const baseStrokes: Style[] = [
      new Style({ stroke: new Stroke({ color: 'rgb(0, 0, 0)', width: 4 }) }),
      new Style({ stroke: new Stroke({ color: activeColor, width: 2 }) })
    ]
    if (!(geom instanceof LineString)) return baseStrokes
    const coords = geom.getCoordinates()
    if (coords.length < 2) return baseStrokes
    const a = coords[coords.length - 2] as [number, number]
    const b = coords[coords.length - 1] as [number, number]
    const dx = b[0] - a[0]
    const dy = b[1] - a[1]
    if (Math.hypot(dx, dy) === 0) return baseStrokes
    return [
      ...baseStrokes,
      ...aHeadingExtension(b, { dx, dy }, activeColor, 2, getHeadingExtensionLength())
    ]
  }
}

const isPolygonFeature = (feature: FeatureLike): boolean => {
  const geometry = feature instanceof RenderFeature ? null : feature.getGeometry()
  return geometry instanceof Polygon || geometry instanceof MultiPolygon
}

/**
 * Style for the drawn zone layer. Paints zone colors during `ZoneEditing`
 * (polygons) and `Lines` (guidance primitives) so each tab only shows the
 * features relevant to it: regions/lines share the zone palette but guidance
 * primitives are hidden outside `Lines` to keep other tabs uncluttered.
 */
export const createZoneLayerStyle = (opts: {
  getActiveZoneId: () => string | undefined
  getResolution: () => number
  getFormatDistance: () => FormatDistance | undefined
  areZonePolygonsHidden: () => boolean
  reflectLassoMode: boolean
}): StyleLike => {
  return (feature: FeatureLike) => {
    if (opts.areZonePolygonsHidden() && isPolygonFeature(feature)) return []
    const scene = getDrawScene()
    const primitiveType = feature.get('type') as GuidancePrimitive | undefined
    if (isNonNil(primitiveType) && scene !== DrawScene.Lines) return []
    if (scene !== DrawScene.ZoneEditing && scene !== DrawScene.Lines) return defaultZoneStyle
    const zone = feature.get('zone') as string | undefined
    if (isNil(zone)) {
      return opts.reflectLassoMode && getDataTools().lassoMode === LassoMode.Exclude
        ? excludeLassoStyle
        : defaultZoneStyle
    }
    const zoneData = getZoneById(zone)
    if (isNil(zoneData)) return defaultZoneStyle
    const isActive = zone === opts.getActiveZoneId()
    const geometry = feature instanceof RenderFeature ? null : feature.getGeometry()
    if (geometry instanceof LineString) {
      // Curve/ABLine come through here (AHeading and RadialPivot have their
      // own per-feature styles). RadialPivot falls back to this branch after
      // a GeoJSON round-trip through the command manager clears the per-
      // feature style, so keep the pivot decoration path.
      const chrome = editableLineChrome({ color: zoneData.color, active: isActive })
      if (primitiveType === GuidancePrimitive.RadialPivot) {
        const pivotStyles = radialPivotStyle(
          geometry,
          zoneData.color,
          isActive,
          opts.getFormatDistance()
        ).map((style) => {
          style.setZIndex(Z_INDEX.DRAW_TOOL + 6)
          return style
        })
        return [...chrome, ...pivotStyles]
      }
      return chrome
    }
    return getStyle({
      color: zoneData.color,
      active: isActive,
      resolution: opts.getResolution()
    })
  }
}

/**
 * Pixel amounts used to spread coincident helm-inspect features (shared
 * borders / shared corners between adjacent regions) toward the polygon they
 * belong to. Corners get a larger push than edges because the marker itself
 * has more area — a small offset visually overlaps the neighbor's dot even
 * when the underlying points have separated, while a thin edge line reads as
 * distinct at a smaller offset.
 */
const EDGE_OFFSET_PX = 3
const CORNER_OFFSET_PX = 14

/**
 * Offset a LineString perpendicular to its overall A→B direction toward
 * `centroidLonLat` by `EDGE_OFFSET_PX * resolution` map units. Used for edge
 * features whose geometry coincides with an adjacent region's edge on a
 * shared border — the offset renders each as a visibly distinct parallel line.
 */
const offsetEdgeGeometry = (
  geom: LineString,
  centroidLonLat: [number, number],
  resolution: number
): LineString => {
  const coords = geom.getCoordinates() as [number, number][]
  if (coords.length < 2) return geom
  const first = coords[0]
  const last = coords[coords.length - 1]
  const dx = last[0] - first[0]
  const dy = last[1] - first[1]
  const len = Math.hypot(dx, dy)
  if (len === 0) return geom
  const perpLeft: [number, number] = [-dy / len, dx / len]
  const centroidProj = fromLonLat(centroidLonLat) as [number, number]
  const midX = (first[0] + last[0]) / 2
  const midY = (first[1] + last[1]) / 2
  const dotLeft = perpLeft[0] * (centroidProj[0] - midX) + perpLeft[1] * (centroidProj[1] - midY)
  const sign = dotLeft >= 0 ? 1 : -1
  const offset = EDGE_OFFSET_PX * resolution * sign
  const offsetX = perpLeft[0] * offset
  const offsetY = perpLeft[1] * offset
  return new LineString(coords.map(([x, y]) => [x + offsetX, y + offsetY]))
}

/**
 * Shift a Point toward `centroidLonLat` by `CORNER_OFFSET_PX * resolution`
 * map units. Used for corner features that would otherwise stack directly on
 * top of a neighbor region's corner at a shared vertex.
 */
const offsetPointCoordinate = (
  coord: [number, number],
  centroidLonLat: [number, number],
  resolution: number
): [number, number] => {
  const centroidProj = fromLonLat(centroidLonLat) as [number, number]
  const vx = centroidProj[0] - coord[0]
  const vy = centroidProj[1] - coord[1]
  const len = Math.hypot(vx, vy)
  if (len === 0) return coord
  const offset = CORNER_OFFSET_PX * resolution
  return [coord[0] + (vx / len) * offset, coord[1] + (vy / len) * offset]
}

/** `kind` under which the propagated guidance preview lives on the view-only layer. */
export const HELM_PREVIEW_KIND = 'helm'

type PreviewStroke = { color: string; lineDash?: number[] }

/**
 * Strokes of the propagated guidance preview. Lightness carries the
 * distinction rather than hue so the preview stays legible over any zone in
 * the zone palette and over satellite imagery.
 */
export const PROPAGATION_PREVIEW_STROKES: {
  INFILL: PreviewStroke
  HEADLAND: PreviewStroke
  INTERIOR_HEADLAND: PreviewStroke
  HEADLAND_BOUNDARY: PreviewStroke
} = {
  INFILL: { color: '#F8FAFC' },
  HEADLAND: { color: '#1F2937' },
  INTERIOR_HEADLAND: { color: '#1F2937', lineDash: [8, 6] },
  HEADLAND_BOUNDARY: { color: '#F8FAFC', lineDash: [6, 6] }
}

const withOpacity = (hex: string, opacity: number): string => {
  const parsed = colorFunc(hex)
  if (isNil(parsed)) return hex
  parsed.opacity = opacity
  return parsed.formatRgb()
}

// Coarser than this (meters per pixel), neighboring infill passes sit only a
// few pixels apart and a full-weight stroke fuses them into a solid wash.
const SWATH_DETAIL_RESOLUTION = 3

const helmStroke = ({
  stroke,
  width,
  opacity = 1
}: {
  stroke: PreviewStroke
  width: number
  opacity?: number
}) =>
  new Style({
    zIndex: Z_INDEX.DRAW_TOOL,
    stroke: new Stroke({
      color: withOpacity(stroke.color, opacity),
      width,
      lineDash: stroke.lineDash
    })
  })

const INFILL_DETAILED = helmStroke({
  stroke: PROPAGATION_PREVIEW_STROKES.INFILL,
  width: 1.25,
  opacity: 0.85
})
const INFILL_COARSE = helmStroke({
  stroke: PROPAGATION_PREVIEW_STROKES.INFILL,
  width: 1,
  opacity: 0.5
})
const HEADLAND = helmStroke({ stroke: PROPAGATION_PREVIEW_STROKES.HEADLAND, width: 2.5 })
const INTERIOR_HEADLAND = helmStroke({
  stroke: PROPAGATION_PREVIEW_STROKES.INTERIOR_HEADLAND,
  width: 2.5
})
const HEADLAND_BOUNDARY = helmStroke({
  stroke: PROPAGATION_PREVIEW_STROKES.HEADLAND_BOUNDARY,
  width: 1.5,
  opacity: 0.9
})

/**
 * Groups omitted here are not drawn: the region outline and seed primitive
 * already render on the editable layer, and boundary sides render as their own
 * view-only kind.
 */
const HELM_GROUP_STYLES: Partial<Record<HelmFeatureGroup, (resolution: number) => Style>> = {
  [HelmFeatureGroup.InfillSwaths]: (resolution) =>
    isSwathDetailResolution(resolution) ? INFILL_DETAILED : INFILL_COARSE,
  [HelmFeatureGroup.HeadlandSwaths]: () => HEADLAND,
  [HelmFeatureGroup.InteriorHeadlandSwaths]: () => INTERIOR_HEADLAND,
  [HelmFeatureGroup.HeadlandBoundary]: () => HEADLAND_BOUNDARY
}

/** Whether infill swaths draw at full weight at this resolution (meters per pixel). */
export const isSwathDetailResolution = (resolution: number): boolean =>
  resolution < SWATH_DETAIL_RESOLUTION

/**
 * Stroke for one group of helm's propagated output, or `undefined` for a group
 * that is not drawn.
 */
export const helmGroupStyle = ({
  group,
  resolution
}: {
  group: HelmFeatureGroup
  resolution: number
}): Style | undefined => HELM_GROUP_STYLES[group]?.(resolution)

const helmPreviewStyle = (feature: FeatureLike, resolution: number): Style[] => {
  const group = helmFeatureGroupSchema.safeParse(feature.get('group'))
  if (!group.success) return []
  const style = helmGroupStyle({ group: group.data, resolution })
  return style === undefined ? [] : [style]
}

/**
 * Style for the view-only overlay layer. Combines two responsibilities:
 *   1) hide scene-scoped inspection kinds (edge/hole/corner) outside their
 *      matching scene, so drawing/editing zones stays uncluttered;
 *   2) paint the feature with its `color` when the current scene allows it,
 *      highlighting whichever feature id is currently selected.
 */
export const createViewOnlyStyle =
  (opts: { getHeadingExtensionLength: () => number }): StyleLike =>
  (feature: FeatureLike, resolution: number) => {
    const color = feature.get('color') as string | undefined
    const kind = feature.get('kind') as string | undefined
    if (kind === HELM_PREVIEW_KIND) return helmPreviewStyle(feature, resolution)
    const highlighted = `${feature.getId()}` === getSelectedId()
    const scene = getDrawScene()
    const geometry = feature instanceof RenderFeature ? null : feature.getGeometry()
    const isPolygon = geometry instanceof Polygon || geometry instanceof MultiPolygon
    const isPoint = geometry instanceof Point
    const regionCentroid = feature.get('regionCentroid') as [number, number] | undefined
    const hasCoincident = feature.get('hasCoincident') === true

    const kindScene = typeof kind === 'string' ? SCENE_FOR_KIND[kind] : undefined
    if (isNonNil(kindScene) && kindScene !== scene && scene !== DrawScene.AllOverrides)
      return new Style({})

    const polygonAllowed =
      !isPolygon ||
      scene === DrawScene.ZoneEditing ||
      scene === DrawScene.Lines ||
      scene === DrawScene.HoleSelection ||
      scene === DrawScene.AllOverrides
    const sceneAllowsColor =
      polygonAllowed &&
      (kindScene === scene ||
        scene === DrawScene.Boundary ||
        scene === DrawScene.AllOverrides ||
        scene === DrawScene.ZoneEditing ||
        scene === DrawScene.Lines)

    if (sceneAllowsColor && typeof color === 'string') {
      if (isPoint) {
        // For corner features whose coordinates coincide with a neighbor
        // region's corner (shared vertex), nudge the rendered marker toward the
        // owning region's centroid so both markers stay visible instead of
        // stacking. Underlying feature geometry is untouched, so hit-testing
        // for picking still lines up with the true corner. Corners that don't
        // share a coordinate with another corner (`hasCoincident` unset by the
        // view-only writer) render exactly on their vertex.
        const coord = (geometry as Point).getCoordinates() as [number, number]
        const offsetCoord =
          kind === 'corner' && regionCentroid !== undefined && hasCoincident
            ? offsetPointCoordinate(coord, regionCentroid, resolution)
            : coord
        const isOverrideCorner = kind === 'corner'
        return new Style({
          zIndex: Z_INDEX.DRAW_TOOL + (highlighted ? 2 : 1),
          geometry: offsetCoord === coord ? undefined : new Point(offsetCoord),
          image: new Circle({
            radius: isOverrideCorner ? (highlighted ? 14 : 8) : highlighted ? 12 : 6,
            fill: new Fill({ color }),
            stroke: new Stroke({ color: '#FFFFFF', width: 3 })
          })
        })
      }
      if (isPolygon) {
        const fillColor = colorFunc(color)
        if (fillColor) fillColor.opacity = highlighted ? 0.55 : 0.35
        // Holes and corridor strips are the two filled override shapes; both
        // get the heavier outline so they read against the zone fill.
        const isOverrideHole = kind === 'hole' || kind === 'corridor'
        return [
          new Style({
            zIndex: Z_INDEX.DRAW_TOOL + 1,
            stroke: new Stroke({
              color: 'white',
              width: isOverrideHole ? (highlighted ? 11 : 4) : highlighted ? 9 : 2
            })
          }),
          new Style({
            zIndex: Z_INDEX.DRAW_TOOL + (highlighted ? 2 : 1),
            stroke: new Stroke({
              color,
              width: isOverrideHole ? (highlighted ? 8 : 4) : highlighted ? 6 : 2
            }),
            fill: new Fill({ color: fillColor ? fillColor.formatRgb() : color })
          })
        ]
      }
      const primitiveType = feature.get('type') as GuidancePrimitive | undefined
      // All saved-line primitives on the Lines tab need to share the same
      // stacking so no type sits behind another (Curve/ABLine already get this
      // via the fall-through branch below). Match its `DRAW_TOOL + (highlighted
      // ? 2 : 1)` for every style AHeading and RadialPivot emit.
      const savedLineZ = Z_INDEX.DRAW_TOOL + (highlighted ? 2 : 1)
      const bumpSaved = (style: Style): Style => {
        style.setZIndex(savedLineZ)
        return style
      }
      if (primitiveType === GuidancePrimitive.AHeading && geometry instanceof LineString) {
        const coords = geometry.getCoordinates()
        if (coords.length >= 2) {
          const anchor = coords[0] as [number, number]
          const dx = coords[1][0] - coords[0][0]
          const dy = coords[1][1] - coords[0][1]
          const lineWidth = highlighted ? 4 : 2
          return [
            bumpSaved(centerMarkerStyle(anchor, color, highlighted)),
            ...(highlighted
              ? aHeadingExtension(
                  anchor,
                  { dx, dy },
                  PRIMITIVE_HALO_COLOR,
                  lineWidth + 8,
                  opts.getHeadingExtensionLength()
                ).map(bumpSaved)
              : []),
            ...aHeadingExtension(
              anchor,
              { dx, dy },
              color,
              lineWidth,
              opts.getHeadingExtensionLength()
            ).map(bumpSaved)
          ]
        }
      }
      if (primitiveType === GuidancePrimitive.RadialPivot && geometry instanceof LineString) {
        const styles = radialPivotStyle(geometry, color, highlighted, undefined).map(bumpSaved)
        if (highlighted) {
          const parts = radialPivotGeom(geometry.getCoordinates())
          if (parts !== undefined) {
            return [
              primitiveHaloStyle({
                geometry: new CircleGeom(parts.center, parts.radius),
                zIndex: savedLineZ
              }),
              ...styles
            ]
          }
        }
        return styles
      }
      const offsetGeom =
        kind === 'edge' &&
        regionCentroid !== undefined &&
        hasCoincident &&
        geometry instanceof LineString
          ? offsetEdgeGeometry(geometry, regionCentroid, resolution)
          : undefined
      const isOverrideEdge = kind === 'edge'
      return [
        new Style({
          zIndex: Z_INDEX.DRAW_TOOL + (highlighted ? 2 : 1),
          stroke: new Stroke({
            color: 'white',
            width: isOverrideEdge ? (highlighted ? 15 : 3) : highlighted ? 13 : 1
          }),
          ...(offsetGeom !== undefined && { geometry: offsetGeom })
        }),
        new Style({
          zIndex: Z_INDEX.DRAW_TOOL + (highlighted ? 2 : 1),
          stroke: new Stroke({
            color,
            width: isOverrideEdge ? (highlighted ? 10 : 5) : highlighted ? 8 : 3
          }),
          ...(offsetGeom !== undefined && { geometry: offsetGeom })
        })
      ]
    }
    if (highlighted) {
      return new Style({
        zIndex: Z_INDEX.DRAW_TOOL + 2,
        stroke: new Stroke({ color: '#FFFFFF', width: 5 })
      })
    }
    return new Style({ stroke: new Stroke({ color: '#000000', width: 1 }) })
  }

/**
 * Default draw-preview style: black outline + colored inline for lines, dashed
 * outline for polygons, colored dot for vertices. Used for every mode except
 * AHeading / RadialPivot, which have bespoke previews.
 */
const defaultDrawStyle = (activeColor: string, fillColor: string) => [
  {
    filter: ['==', ['geometry-type'], 'LineString'],
    style: [
      { 'stroke-color': 'rgb(0, 0, 0)', 'stroke-width': 4 },
      { 'stroke-color': activeColor, 'stroke-width': 2 }
    ]
  },
  {
    filter: ['==', ['geometry-type'], 'Polygon'],
    style: {
      'stroke-color': 'rgba(0, 0, 0, 0.5)',
      'stroke-width': 3,
      'stroke-line-dash': [4, 20],
      'fill-color': fillColor
    }
  },
  {
    else: true,
    style: {
      'circle-radius': 4,
      'circle-fill-color': activeColor
    }
  }
]

/**
 * Dispatch for the draw-preview style passed to the OL `Draw` interaction.
 * AHeading and RadialPivot get bespoke previews; every other mode falls back
 * to `defaultDrawStyle`.
 */
export const createPrimitiveDrawStyle = (opts: {
  mode: DrawMode
  activeColor: string
  fillColor: string
  getFormatDistance?: () => FormatDistance | undefined
  getHeadingExtensionLength: () => number
}) => {
  switch (opts.mode) {
    case DrawMode.AHeading:
      return aHeadingDrawStyle(opts.activeColor, opts.getHeadingExtensionLength)
    case DrawMode.RadialPivot:
      return radialPivotDrawStyle(opts.activeColor, opts.getFormatDistance)
    default:
      return defaultDrawStyle(opts.activeColor, opts.fillColor)
  }
}
